import { createContext, useContext, useMemo } from 'react';
import { useClients } from './ClientsContext.jsx';
import { useClientMarketplaceFilter } from '../components/shared/ClientMarketplaceFilter.jsx';

const ClientFilterContext = createContext({
  clients: [],
  clientId: '',
  marketplace: '',
  marketplaces: [],
  client: undefined,
  setClientId: () => {},
  setMarketplace: () => {},
});

// Owns the client + marketplace URL state for the entire app.
// Must be mounted inside <BrowserRouter> and <ClientsProvider>.
export function ClientFilterProvider({ children }) {
  const { clients: allClients, loading } = useClients();
  // Exclude Left clients from the picker — they're archived, not actively managed.
  const clients = useMemo(
    () => allClients.filter((c) => c.overall_status !== 'Left'),
    [allClients]
  );
  const filter = useClientMarketplaceFilter({ clients });

  // While the clients list is still loading, mask clientId/marketplace as empty.
  // Without this, pages render with clientId set (from URL/localStorage) but
  // client=undefined (not yet in the array), causing TypeErrors in render paths
  // that assume a clientId means the client object is available.
  const value = useMemo(
    () => ({
      clients,
      loading,
      ...filter,
      ...(loading && {
        clientId: '',
        marketplace: '',
        client: undefined,
        marketplaces: [],
      }),
    }),
    [clients, loading, filter.clientId, filter.marketplace, filter.client, filter.marketplaces, filter.setClientId, filter.setMarketplace]
  );

  return (
    <ClientFilterContext.Provider value={value}>
      {children}
    </ClientFilterContext.Provider>
  );
}

export function useClientFilter() {
  return useContext(ClientFilterContext);
}
